import { Capacitor } from "@capacitor/core";
import { TextToSpeech } from "@capacitor-community/text-to-speech";
import { supabase } from "@/integrations/supabase/client";
import { getTTSVoiceIndex } from "@/lib/ttsVoice";
import { endAudioDucking, playDuckedNativeAudio } from "@/lib/straivNowPlaying";

/**
 * Cloud TTS (OpenAI/ElevenLabs via coach-voice-tts) for AI coach / in-run guidance.
 * speakCoachCue never falls back to on-device voice.
 * speakNativeNow is for countdown, normal-run stats, pause/resume/finish only.
 */

export type SpeakOptions = {
  voice?: "male" | "female";
  /** Default true — OpenAI/ElevenLabs only. Set false only for emergency offline. */
  useNatural?: boolean;
  /** When true, clear the queue and speak immediately. */
  interrupt?: boolean;
  /** Jump ahead of queued cues (e.g. "run started" before coach intro). */
  priority?: boolean;
  /**
   * When false (default for speakCoachCue), never fall back to on-device voice
   * if cloud TTS fails — coach audio must stay natural or be skipped.
   */
  nativeFallback?: boolean;
};

const NATURAL_TIMEOUT_MS = 8000;

let currentAudio: HTMLAudioElement | null = null;
let cachedAvailable: boolean | null = null;

/** Locked TTS profile for the current coach run — prevents ElevenLabs/OpenAI switching mid-run. */
let lockedVoiceProfile: { provider: string; voiceId?: string } | null = null;

type NaturalFetchResult = { mime: string; b64: string; provider?: string; voiceId?: string };

type QueueItem = {
  text: string;
  options?: SpeakOptions;
  resolve: () => void;
  priority?: boolean;
};

const speechQueue: QueueItem[] = [];
let processingQueue = false;

const stopAudioElement = () => {
  try {
    if (currentAudio) {
      currentAudio.onended = null;
      currentAudio.onerror = null;
      currentAudio.pause();
      currentAudio.src = "";
      currentAudio = null;
    }
  } catch {}
};

const stopNativeTts = () => {
  try {
    if (Capacitor.isNativePlatform()) {
      void TextToSpeech.stop().catch(() => undefined);
    } else if (typeof window !== "undefined" && "speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
  } catch {}
};

const stopAllPlayback = () => {
  stopAudioElement();
  stopNativeTts();
};

const base64ToBlob = (b64: string, mime: string): Blob => {
  const byteChars = atob(b64);
  const len = byteChars.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = byteChars.charCodeAt(i);
  return new Blob([bytes], { type: mime });
};

async function speakNative(text: string, voice: "male" | "female"): Promise<void> {
  stopNativeTts();
  if (Capacitor.isNativePlatform()) {
    const voiceIndex = await getTTSVoiceIndex(voice);
    const opts: Record<string, unknown> = {
      text,
      lang: "en-US",
      rate: 0.92,
      pitch: voice === "male" ? 0.95 : 1.02,
      volume: 1.0,
      queueStrategy: 1,
      duckOthers: true,
      ...(voiceIndex !== undefined && voiceIndex >= 0 && { voice: voiceIndex }),
    };
    await TextToSpeech.speak(opts as unknown as Parameters<typeof TextToSpeech.speak>[0]).catch(() => undefined);
    return;
  }

  if (typeof window !== "undefined" && "speechSynthesis" in window) {
    await new Promise<void>((resolve) => {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.92;
      utterance.pitch = voice === "male" ? 0.95 : 1.05;
      utterance.volume = 1.0;
      utterance.onend = () => resolve();
      utterance.onerror = () => resolve();
      window.speechSynthesis.speak(utterance);
    });
  }
}

async function fetchNatural(
  text: string,
  voice: "male" | "female",
  opts?: { ignoreCache?: boolean },
): Promise<NaturalFetchResult | null> {
  if (!opts?.ignoreCache && cachedAvailable === false) return null;
  try {
    const body: Record<string, unknown> = { text, voice };
    if (lockedVoiceProfile) {
      body.lockProvider = lockedVoiceProfile.provider;
      if (lockedVoiceProfile.voiceId) body.lockVoiceId = lockedVoiceProfile.voiceId;
    }
    const { data, error } = await supabase.functions.invoke("coach-voice-tts", {
      body,
    });
    const d = data as {
      provider?: string;
      voice_id?: string;
      mime?: string;
      audio_base64?: string;
      reason?: string;
    };
    if (error || !d?.audio_base64 || !d?.mime) {
      console.warn("[NaturalVoice] cloud TTS unavailable:", error ?? d?.reason ?? "no audio");
      if (d?.reason === "no_tts_provider_configured") cachedAvailable = false;
      return null;
    }
    cachedAvailable = true;
    if (d.provider && !lockedVoiceProfile) {
      lockedVoiceProfile = { provider: d.provider, voiceId: d.voice_id };
      console.log("[NaturalVoice] Locked coach voice:", lockedVoiceProfile);
    }
    return { mime: d.mime, b64: d.audio_base64, provider: d.provider, voiceId: d.voice_id };
  } catch (e) {
    console.warn("[NaturalVoice] fetch failed:", e);
    return null;
  }
}

/** Cloud TTS with retries — used for all coach / AI guide speech. */
async function fetchNaturalWithRetry(
  text: string,
  voice: "male" | "female",
  attempts = 3,
): Promise<{ mime: string; b64: string } | null> {
  for (let i = 0; i < attempts; i++) {
    const result = await fetchNatural(text, voice, { ignoreCache: i > 0 });
    if (result) return result;
    if (i < attempts - 1) {
      await new Promise((r) => setTimeout(r, 400 * (i + 1)));
    }
  }
  return null;
}

async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      const b64 = result.includes(",") ? result.split(",")[1] : result;
      resolve(b64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function playBlob(blob: Blob): Promise<void> {
  const useNativeIos =
    Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";

  if (useNativeIos) {
    return blobToBase64(blob).then(async (b64) => {
      await playDuckedNativeAudio(b64, blob.type || "audio/mpeg");
    });
  }

  return new Promise((resolve) => {
    try {
      stopAudioElement();
      const audio = new Audio();
      currentAudio = audio;
      audio.preload = "auto";
      audio.src = URL.createObjectURL(blob);
      const cleanup = () => {
        try {
          if (audio.src) URL.revokeObjectURL(audio.src);
        } catch {}
        if (currentAudio === audio) currentAudio = null;
        resolve();
      };
      audio.onended = cleanup;
      audio.onerror = cleanup;
      void audio.play().catch((err) => {
        console.warn("[NaturalVoice] audio.play failed:", err);
        cleanup();
      });
    } catch (e) {
      console.warn("[NaturalVoice] playBlob threw:", e);
      resolve();
    }
  });
}

async function speakOne(text: string, options?: SpeakOptions): Promise<void> {
  const clean = String(text || "").trim();
  if (!clean) return;
  const voice = options?.voice === "male" ? "male" : "female";
  const useCloud = options?.useNatural !== false;
  // Prefer cloud voice; fall back to on-device when cloud is unavailable so coach still speaks.
  const allowNativeFallback = options?.nativeFallback !== false;

  if (useCloud) {
    stopNativeTts();
    const natural = await fetchNaturalWithRetry(clean, voice);
    if (natural) {
      const blob = base64ToBlob(natural.b64, natural.mime);
      await playBlob(blob);
      return;
    }
    if (allowNativeFallback) {
      console.warn("[NaturalVoice] cloud TTS failed, using on-device voice:", clean.slice(0, 40));
      await speakNative(clean, voice);
    } else {
      console.warn("[NaturalVoice] cloud TTS failed, skipping cue (no robot fallback):", clean.slice(0, 40));
    }
    return;
  }

  await speakNative(clean, voice);
}

async function drainQueue(): Promise<void> {
  if (processingQueue) return;
  processingQueue = true;
  try {
    while (speechQueue.length > 0) {
      const item = speechQueue.shift()!;
      try {
        await speakOne(item.text, item.options);
      } catch (e) {
        console.warn("[NaturalVoice] speakOne failed:", e);
      }
      item.resolve();
    }
  } finally {
    processingQueue = false;
  }
}

/** Speak via cloud TTS (queued). Returns when playback finishes. */
export function speakCoachCue(text: string, options?: SpeakOptions): Promise<void> {
  const clean = String(text || "").trim();
  if (!clean) return Promise.resolve();

  if (options?.interrupt) {
    speechQueue.length = 0;
    stopAllPlayback();
  }

  return new Promise<void>((resolve) => {
    const item: QueueItem = {
      text: clean,
      options: { ...options, nativeFallback: true, useNatural: true },
      resolve,
      priority: options?.priority === true,
    };
    if (item.priority) speechQueue.unshift(item);
    else speechQueue.push(item);
    void drainQueue();
  });
}

export function stopCoachVoice(): void {
  speechQueue.length = 0;
  stopAllPlayback();
  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios") {
    endAudioDucking();
  }
}

export function resetCoachVoiceSession(): void {
  cachedAvailable = null;
  lockedVoiceProfile = null;
}

/** @deprecated use resetCoachVoiceSession */
export function resetNaturalVoiceCache(): void {
  resetCoachVoiceSession();
}

/**
 * Pre-fetch a TTS clip as a Blob without queuing/playback. Useful for warming
 * up the cloud TTS function and pre-buffering audio (e.g. for countdown so
 * playback is instant once the visual shows the digit).
 */
export async function prefetchCoachAudio(
  text: string,
  voice: "male" | "female" = "female",
): Promise<Blob | null> {
  const clean = String(text || "").trim();
  if (!clean) return null;
  const natural = await fetchNaturalWithRetry(clean, voice);
  if (!natural) return null;
  return base64ToBlob(natural.b64, natural.mime);
}

/**
 * Play a pre-fetched Blob immediately (no queue, no cloud round-trip).
 * Resolves when the audio finishes.
 */
export function playCoachAudio(blob: Blob | null): Promise<void> {
  if (!blob) return Promise.resolve();
  stopNativeTts();
  return playBlob(blob);
}

/**
 * Speak via on-device voice immediately (no queue, no cloud).
 * Always resolves when speech finishes or fails.
 */
export function speakNativeNow(
  text: string,
  voice: "male" | "female" = "female",
): Promise<void> {
  const clean = String(text || "").trim();
  if (!clean) return Promise.resolve();
  return speakNative(clean, voice);
}
